import { format, formatDistanceToNow, parse } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Loader2,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { useAgentLogs, useAgentMetrics } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const DANGER_COLOR = 'hsl(0, 84%, 60%)';
const WARNING_COLOR = 'hsl(38, 92%, 50%)';
const INFO_COLOR = 'hsl(217, 91%, 60%)';

const AGENT_COLORS: Record<string, string> = {
  lead_qualification: 'hsl(184, 92%, 56%)',
  lead_enrichment: 'hsl(217, 91%, 60%)',
  follow_up_scheduler: 'hsl(142, 71%, 45%)',
  sentiment_analysis: 'hsl(280, 67%, 60%)',
  stage_prospect_intake: 'hsl(38, 92%, 50%)',
  stage_first_contact: 'hsl(160, 60%, 45%)',
  stage_qualification_deep: 'hsl(340, 82%, 52%)',
  stage_proposal_prep: 'hsl(200, 98%, 48%)',
  stage_won_onboarding: 'hsl(120, 60%, 50%)',
  stage_lost_analysis: 'hsl(0, 84%, 60%)',
  orchestrator: 'hsl(263, 70%, 60%)',
};

const STATUS_CONFIG: Record<
  string,
  {
    color: string;
    icon: typeof CheckCircle2;
    variant: 'success' | 'danger' | 'warning' | 'info' | 'default';
  }
> = {
  completed: { color: SUCCESS_COLOR, icon: CheckCircle2, variant: 'success' },
  failed: { color: DANGER_COLOR, icon: XCircle, variant: 'danger' },
  running: { color: INFO_COLOR, icon: Loader2, variant: 'info' },
  queued: { color: WARNING_COLOR, icon: Clock, variant: 'warning' },
  cancelled: { color: AXIS_STROKE, icon: XCircle, variant: 'default' },
};

function formatAgentType(type: string) {
  return type.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(ms: number | null) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

const DAYS_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

const STATUS_OPTIONS = ['', 'completed', 'failed', 'running', 'queued', 'cancelled'];
const AGENT_TYPE_OPTIONS = [
  '',
  'lead_qualification',
  'lead_enrichment',
  'follow_up_scheduler',
  'sentiment_analysis',
  'stage_prospect_intake',
  'stage_first_contact',
  'stage_qualification_deep',
  'stage_proposal_prep',
  'stage_won_onboarding',
  'stage_lost_analysis',
  'orchestrator',
];

export function AgentMonitoring() {
  const [days, setDays] = useState(30);
  const [logsPage, setLogsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [agentTypeFilter, setAgentTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: metrics, isLoading: metricsLoading } = useAgentMetrics({ days });

  // Optional on the type: the panel has to render against an API that has not
  // shipped these fields yet.
  const recentDegradations = metrics?.recentDegradations ?? [];
  const degradationsByCause = metrics?.degradationsByCause ?? [];
  const { data: logs, isLoading: logsLoading } = useAgentLogs({
    page: logsPage,
    limit: 20,
    ...(statusFilter && { status: statusFilter }),
    ...(agentTypeFilter && { agentType: agentTypeFilter }),
    ...(searchQuery && { search: searchQuery }),
  });

  const summary = metrics?.summary;

  const dailyChartData =
    metrics?.dailyExecutions.map((d) => ({
      date: format(parse(d.date, 'yyyy-MM-dd', new Date()), 'MMM d'),
      total: d.total,
      completed: d.completed,
      failed: d.failed,
    })) ?? [];

  const agentTypeChartData =
    metrics?.byAgentType.map((a) => ({
      name: formatAgentType(a.agentType),
      agentType: a.agentType,
      count: a.count,
      cost: parseFloat(a.costUsd),
    })) ?? [];

  const statusPieData = summary
    ? [
        { name: 'Completed', value: summary.completed, color: SUCCESS_COLOR },
        { name: 'Failed', value: summary.failed, color: DANGER_COLOR },
        { name: 'Running', value: summary.running, color: INFO_COLOR },
        { name: 'Queued', value: summary.queued, color: WARNING_COLOR },
        { name: 'Cancelled', value: summary.cancelled, color: AXIS_STROKE },
      ].filter((s) => s.value > 0)
    : [];

  const totalPages = logs ? Math.ceil(logs.total / 20) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Agent Monitoring</h1>
          <p className="text-sm text-text-muted mt-1">Real-time agent execution metrics and logs</p>
        </div>
        <div className="flex items-center gap-3">
          {summary && summary.running > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-info/10 border border-info/30 rounded-lg">
              <Loader2 size={14} className="text-info animate-spin" />
              <span className="text-sm text-info font-medium">{summary.running} running</span>
            </div>
          )}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
          >
            {DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          label="Total Executions"
          value={summary?.totalExecutions ?? 0}
          icon={Zap}
          loading={metricsLoading}
        />
        <StatCard
          label="Success Rate"
          value={summary ? `${summary.successRate}%` : '0%'}
          icon={TrendingUp}
          loading={metricsLoading}
        />
        <StatCard
          label="Failure Rate"
          value={summary ? `${summary.failureRate}%` : '0%'}
          icon={TrendingDown}
          loading={metricsLoading}
        />
        {/* Runs that completed without ever reaching a model. These used to be
            folded into Success Rate, hiding silent degradation entirely. */}
        <StatCard
          label="Fallback Rate"
          value={summary ? `${summary.fallbackRate ?? 0}%` : '0%'}
          icon={ShieldAlert}
          loading={metricsLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(summary?.avgDurationMs ?? 0)}
          icon={Clock}
          loading={metricsLoading}
        />
        <StatCard
          label="Total Cost"
          value={summary ? `$${parseFloat(summary.totalCostUsd).toFixed(2)}` : '$0.00'}
          icon={DollarSign}
          loading={metricsLoading}
        />
      </div>

      {/* Status breakdown mini cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(['completed', 'failed', 'running', 'queued', 'cancelled'] as const).map((status) => {
          const config = STATUS_CONFIG[status];
          const count = summary?.[status] ?? 0;
          const Icon = config.icon;
          return (
            <div
              key={status}
              className="flex items-center gap-3 bg-surface rounded-lg border border-border p-3"
            >
              <Icon
                size={18}
                style={{ color: config.color }}
                className={status === 'running' ? 'animate-spin' : ''}
              />
              <div>
                <div className="text-lg font-semibold text-text">
                  {metricsLoading ? '-' : count}
                </div>
                <div className="text-xs text-text-muted capitalize">{status}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily executions trend */}
        <div className="lg:col-span-2 bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Execution Trends</h2>
          {metricsLoading ? (
            <ChartSkeleton />
          ) : dailyChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyChartData}>
                <defs>
                  <linearGradient id="completedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SUCCESS_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SUCCESS_COLOR} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="failedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={DANGER_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={DANGER_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={12} />
                <YAxis stroke={AXIS_STROKE} fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="completed"
                  stroke={SUCCESS_COLOR}
                  strokeWidth={2}
                  fill="url(#completedGrad)"
                  name="Completed"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke={DANGER_COLOR}
                  strokeWidth={2}
                  fill="url(#failedGrad)"
                  name="Failed"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">
              No execution data for this period
            </div>
          )}
        </div>

        {/* Status distribution pie */}
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Status Distribution</h2>
          {metricsLoading ? (
            <ChartSkeleton />
          ) : statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                  label={({ name, value }) => `${name} (${value})`}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      {/* Agent type breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Executions by Agent Type</h2>
          {metricsLoading ? (
            <ChartSkeleton />
          ) : agentTypeChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={agentTypeChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis type="number" stroke={AXIS_STROKE} fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={AXIS_STROKE}
                  fontSize={11}
                  width={160}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Executions" radius={[0, 4, 4, 0]}>
                  {agentTypeChartData.map((entry) => (
                    <Cell
                      key={entry.agentType}
                      fill={AGENT_COLORS[entry.agentType] ?? PRIMARY_COLOR}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        {/* Recent failures */}
        <div className="bg-surface rounded-xl border border-border">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <AlertTriangle size={16} className="text-danger" />
            <h2 className="text-sm font-medium text-text-secondary">Recent Failures</h2>
          </div>
          <div className="divide-y divide-border max-h-[340px] overflow-y-auto">
            {metricsLoading ? (
              ['s1', 's2', 's3', 's4', 's5'].map((sk) => (
                <div key={sk} className="px-5 py-3 animate-pulse">
                  <div className="h-4 bg-border rounded w-48 mb-2" />
                  <div className="h-3 bg-border rounded w-32" />
                </div>
              ))
            ) : metrics?.recentFailures?.length ? (
              metrics.recentFailures.map((failure) => (
                <div
                  key={failure.id}
                  className="px-5 py-3 hover:bg-surface-hover transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-text">
                      {formatAgentType(failure.agentType)}
                    </span>
                    <span className="text-xs text-text-muted">
                      {formatDistanceToNow(new Date(failure.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-xs text-danger truncate">
                    {failure.errorMessage ?? 'Unknown error'}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">{failure.organizationName}</p>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-text-muted text-sm">
                No recent failures
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Degraded runs. A fallback completes, so it is invisible above — during
          the 2026-08-01 credit outage this panel is the only place the cause
          appears without opening the database. */}
      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <AlertTriangle size={16} className="text-warning" />
          <h2 className="text-sm font-medium text-text-secondary">
            Degraded Runs (rule-based, never reached a model)
          </h2>
        </div>

        {degradationsByCause.length > 0 && (
          <div className="px-5 py-4 border-b border-border flex flex-wrap gap-2">
            {degradationsByCause.map((entry) => (
              <div
                key={entry.cause}
                className="flex items-center gap-2 rounded-lg border border-border bg-surface-hover px-3 py-2"
              >
                <span className="text-xs font-mono text-warning">{entry.cause}</span>
                <span className="text-sm font-semibold text-text">{entry.count}</span>
                <span className="text-xs text-text-muted">
                  {entry.organizations === 1 ? '1 org' : `${entry.organizations} orgs`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="divide-y divide-border max-h-[340px] overflow-y-auto">
          {recentDegradations.length ? (
            recentDegradations.map((degradation) => (
              <div
                key={degradation.id}
                className="px-5 py-3 hover:bg-surface-hover transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text">
                    {formatAgentType(degradation.agentType)}
                  </span>
                  <span className="text-xs text-text-muted">
                    {formatDistanceToNow(new Date(degradation.createdAt), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-xs text-warning break-words">
                  {degradation.errorMessage ?? 'Cause not recorded'}
                </p>
                <p className="text-xs text-text-muted mt-0.5">{degradation.organizationName}</p>
              </div>
            ))
          ) : (
            <div className="px-5 py-8 text-center text-text-muted text-sm">No degraded runs</div>
          )}
        </div>
      </div>

      {/* Execution logs table */}
      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-primary" />
              <h2 className="text-sm font-medium text-text-secondary">Execution Logs</h2>
              {logs && <span className="text-xs text-text-muted">({logs.total} total)</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setLogsPage(1);
              }}
              placeholder="Search by lead or error..."
              className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary w-64"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setLogsPage(1);
              }}
              className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
            <select
              value={agentTypeFilter}
              onChange={(e) => {
                setAgentTypeFilter(e.target.value);
                setLogsPage(1);
              }}
              aria-label="Filter by agent type"
              className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
            >
              <option value="">All agents</option>
              {AGENT_TYPE_OPTIONS.filter(Boolean).map((t) => (
                <option key={t} value={t}>
                  {formatAgentType(t)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Agent</th>
                <th className="px-5 py-3 font-medium">Organization</th>
                <th className="px-5 py-3 font-medium">Lead</th>
                <th className="px-5 py-3 font-medium">Model</th>
                <th className="px-5 py-3 font-medium">Duration</th>
                <th className="px-5 py-3 font-medium">Tokens</th>
                <th className="px-5 py-3 font-medium">Cost</th>
                <th className="px-5 py-3 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                ['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8', 'r9', 'r10'].map((rk) => (
                  <tr key={rk} className="border-b border-border animate-pulse">
                    {['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9'].map((ck) => (
                      <td key={ck} className="px-5 py-3">
                        <div className="h-4 bg-border rounded w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs?.data?.length ? (
                logs.data.map((log) => {
                  const statusCfg = STATUS_CONFIG[log.status] ?? STATUS_CONFIG.cancelled;
                  const StatusIcon = statusCfg.icon;
                  return (
                    <tr
                      key={log.id}
                      className="border-b border-border hover:bg-surface-hover transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusIcon
                            size={14}
                            style={{ color: statusCfg.color }}
                            className={log.status === 'running' ? 'animate-spin' : ''}
                          />
                          <Badge variant={statusCfg.variant}>{log.status}</Badge>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-text font-medium">
                        {formatAgentType(log.agentType)}
                        {log.toolCallCount ? (
                          <span
                            className="block text-xs font-normal text-text-muted"
                            title={log.toolNames?.join(', ')}
                          >
                            {log.toolCallCount === 1
                              ? '1 tool call'
                              : `${log.toolCallCount} tool calls`}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/organizations/${log.organizationId}`)}
                          className="text-text-secondary hover:text-primary transition-colors"
                        >
                          {log.organizationName}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-text-secondary truncate max-w-[140px]">
                        {log.leadName ?? '-'}
                      </td>
                      <td className="px-5 py-3 text-text-muted text-xs">{log.modelUsed ?? '-'}</td>
                      <td className="px-5 py-3 text-text-secondary">
                        {formatDuration(log.durationMs)}
                      </td>
                      <td className="px-5 py-3 text-text-secondary text-xs">
                        {log.inputTokens + log.outputTokens > 0
                          ? `${log.inputTokens.toLocaleString()} / ${log.outputTokens.toLocaleString()}`
                          : '-'}
                      </td>
                      <td className="px-5 py-3 text-text-secondary">
                        {parseFloat(log.costUsd) > 0
                          ? `$${parseFloat(log.costUsd).toFixed(4)}`
                          : '-'}
                      </td>
                      <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                        {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-5 py-8 text-center text-text-muted">
                    No agent executions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="text-sm text-text-muted">
              Page {logsPage} of {totalPages} ({logs?.total} results)
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                disabled={logsPage === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setLogsPage((p) => Math.min(totalPages, p + 1))}
                disabled={logsPage >= totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
